// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/avatar.tsx (look-alike: no Radix)
// NEW local look-alike (plan P4-2): `@radix-ui/react-avatar` is not a dependency of this app and none may be added, so
// this file exports the SAME names and props as TP's avatar (Avatar with `kind` / `size` / `hue` / `initials` / `label`,
// AvatarImage, AvatarFallback, avatarVariants, ORB_HUES, orbStyle), with the same classes.
//
// Contract (Radix's, the subset TP uses): AvatarImage is the picture; AvatarFallback shows until the picture has
// loaded, and for good when it fails to load or has no `src`. Each Avatar keeps its own image status.
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { dsVar } from '@/design-system/tokens';
import { cn } from '@/lib/utils';

/**
 * DESIGN_SYSTEM.md §6 "Agents": orbs for agents, initials for people, pixel
 * marks (`PixelMark`) for agent-made objects. A pill radius is allowed here
 * (Law 4.2: dots, avatars, orbs).
 */
const avatarVariants = cva('relative flex shrink-0 overflow-hidden', {
  variants: {
    kind: {
      /** The registry's avatar, untouched. */
      default: 'h-10 w-10 rounded-full',
      /** A person: initials on 10% ink. */
      person: 'items-center justify-center rounded-pill bg-ds-ink-a10 text-label text-foreground',
      /** An agent: the orb. Its gradient is inline, from the orb tokens. */
      agent: 'rounded-pill',
    },
    /** Only `person` and `agent` take a size (the compound variants below); `default` keeps the registry's 40px whatever `size` says. */
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  compoundVariants: [
    { kind: ['person', 'agent'], size: 'sm', className: 'size-6' },
    { kind: ['person', 'agent'], size: 'md', className: 'size-8' },
    { kind: ['person', 'agent'], size: 'lg', className: 'size-10' },
  ],
  defaultVariants: { kind: 'default', size: 'md' },
});

export const ORB_HUES = ['violet', 'green', 'rose', 'orange', 'blue'] as const;
export type OrbHue = (typeof ORB_HUES)[number];

/**
 * The orb's inline style: `orb-<hue>-a` lit at the top left into `orb-<hue>-b`,
 * a 1px rim light and a bottom shade. The values are `var(--ds-…)` references.
 */
export function orbStyle(hue: OrbHue): React.CSSProperties {
  const a = dsVar(`color.orb-${hue}-a` as const);
  const b = dsVar(`color.orb-${hue}-b` as const);
  const rim = dsVar('color.accent-ink');
  const shade = dsVar('color.scrim');
  return {
    background: `radial-gradient(circle at 30% 30%, ${a}, ${b})`,
    boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${rim} 14%, transparent), inset 0 -6px 12px color-mix(in srgb, ${shade} 50%, transparent)`,
  };
}

type ImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface AvatarContextValue {
  status: ImageStatus;
  setStatus: (status: ImageStatus) => void;
}

const AvatarContext = React.createContext<AvatarContextValue | null>(null);

function useAvatarContext(part: string): AvatarContextValue {
  const ctx = React.useContext(AvatarContext);
  if (!ctx) throw new Error(`${part} must be used within <Avatar>`);
  return ctx;
}

export interface AvatarProps extends React.ComponentPropsWithoutRef<'span'>, VariantProps<typeof avatarVariants> {
  /** `kind="agent"`: which orb. Default violet. */
  hue?: OrbHue;
  /** `kind="person"` without an image: the initials to show. */
  initials?: string;
  /**
   * The name of who the avatar stands for, for assistive tech: `role="img"` +
   * `aria-label`. For `kind="agent"` without it the orb is decorative
   * (`aria-hidden`); for `kind="person"` without it the initials are
   * `aria-hidden` too, identity coming from the name beside the avatar. The
   * registry `default` kind is untouched.
   */
  label?: string;
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, kind = 'default', size = 'md', hue = 'violet', initials, label, style, children, ...props }, ref) => {
    const [status, setStatus] = React.useState<ImageStatus>('idle');
    const value = React.useMemo(() => ({ status, setStatus }), [status]);
    const isAgent = kind === 'agent';
    const named = kind !== 'default' && Boolean(label);
    return (
      <AvatarContext.Provider value={value}>
        <span
          ref={ref}
          data-slot="avatar"
          data-variant={kind}
          data-hue={isAgent ? hue : undefined}
          role={named ? 'img' : undefined}
          aria-label={named ? label : undefined}
          aria-hidden={isAgent && !label ? true : undefined}
          style={isAgent ? { ...orbStyle(hue), ...style } : style}
          className={cn(avatarVariants({ kind, size }), className)}
          {...props}
        >
          {kind === 'person' && initials !== undefined && children === undefined ? <span aria-hidden="true">{initials}</span> : children}
        </span>
      </AvatarContext.Provider>
    );
  },
);
Avatar.displayName = 'Avatar';

const AvatarImage = React.forwardRef<HTMLImageElement, React.ComponentPropsWithoutRef<'img'>>(
  ({ className, src, alt = '', onLoad, onError, ...props }, ref) => {
    const { status, setStatus } = useAvatarContext('AvatarImage');
    const hasSrc = typeof src === 'string' && src.trim() !== '';
    const imgRef = React.useRef<HTMLImageElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLImageElement | null) => {
        imgRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // A new picture starts over; no picture at all is a failed one (the fallback stays). A cached picture can have
    // loaded (its `onLoad` already fired) before this effect runs, so look at the element first: starting over at
    // `loading` would overwrite that `loaded` and leave the fallback showing through the picture.
    React.useEffect(() => {
      const img = imgRef.current;
      if (!hasSrc) setStatus('error');
      else setStatus(img?.complete && img.naturalWidth > 0 ? 'loaded' : 'loading');
      return () => setStatus('idle');
    }, [src, hasSrc, setStatus]);

    if (!hasSrc || status === 'error') return null;
    return (
      <img
        ref={setRefs}
        src={src}
        alt={alt}
        // Over the fallback until it has loaded; once it has, the fallback is gone.
        className={cn('absolute inset-0 aspect-square h-full w-full object-cover', className)}
        onLoad={(event) => {
          setStatus('loaded');
          onLoad?.(event);
        }}
        onError={(event) => {
          setStatus('error');
          onError?.(event);
        }}
        {...props}
      />
    );
  },
);
AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<'span'>>(({ className, ...props }, ref) => {
  const { status } = useAvatarContext('AvatarFallback');
  if (status === 'loaded') return null;
  return (
    <span
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full bg-muted [[data-variant=person]>&]:rounded-pill [[data-variant=person]>&]:bg-transparent',
        className,
      )}
      {...props}
    />
  );
});
AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarImage, AvatarFallback, avatarVariants };
